import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import './UserSettings.css';

const UserSettings = ({ currentUser }) => {
  const [smsOptIn, setSmsOptIn] = useState(currentUser.smsOptIn || false);
  const [mobileNumber, setMobileNumber] = useState(currentUser.mobileNumber || '');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Debug: Log currentUser structure
    console.log('🔍 UserSettings - currentUser:', {
      uid: currentUser.uid,
      email: currentUser.email,
      firestoreDocId: currentUser.firestoreDocId,
      docId: currentUser.docId,
      userType: currentUser.userType,
      role: currentUser.role,
      mobileNumber: currentUser.mobileNumber
    });

    setSmsOptIn(currentUser.smsOptIn || false);
    setMobileNumber(currentUser.mobileNumber || '');
  }, [currentUser]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setMobileNumber(formatted);
  };

  // Validate that it's a mobile number (US)
  const validateMobileNumber = (phone) => {
    // Remove formatting
    const numbers = phone.replace(/\D/g, '');
    
    // Must be exactly 10 digits
    if (numbers.length !== 10) {
      return { valid: false, message: 'Mobile number must be 10 digits' };
    }

    // Check if it's likely a mobile number (not starting with common landline patterns)
    // This is a basic check - US mobile numbers typically don't start with certain area codes
    const areaCode = numbers.slice(0, 3);
    const landlineAreaCodes = ['800', '888', '877', '866', '855', '844', '833']; // Toll-free
    
    if (landlineAreaCodes.includes(areaCode)) {
      return { valid: false, message: 'Please enter a mobile phone number, not a toll-free or landline number' };
    }

    return { valid: true };
  };

  const handleSavePhone = async () => {
    console.log('📱 Starting mobile number save...');
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Validate mobile number
      const validation = validateMobileNumber(mobileNumber);
      if (!validation.valid) {
        console.error('❌ Validation failed:', validation.message);
        setErrorMessage(validation.message);
        setLoading(false);
        return;
      }

      // Convert to E.164 format for Twilio (+1XXXXXXXXXX)
      const numbers = mobileNumber.replace(/\D/g, '');
      const e164Phone = `+1${numbers}`;

      console.log('📱 Mobile number to save:', e164Phone);

      // Determine which collection the user is in
      const collectionName = currentUser.userType === 'volunteer' ? 'volunteers' : 'faculty_staff';
      const documentId = currentUser.firestoreDocId || currentUser.docId;

      console.log('📱 Update details:', {
        collection: collectionName,
        documentId: documentId,
        hasDocumentId: !!documentId
      });

      if (!documentId) {
        console.error('❌ No document ID found!');
        setErrorMessage('Error: Cannot identify your user record. Please contact support.');
        setLoading(false);
        return;
      }

      const userRef = doc(db, collectionName, documentId);
      console.log('📱 Document reference created:', userRef.path);
      
      const updateData = {
        mobileNumber: e164Phone,
        mobileNumberUpdatedAt: serverTimestamp(),
      };

      console.log('📱 Attempting to update with data:', updateData);

      await updateDoc(userRef, updateData);

      console.log('✅ Mobile number updated successfully in Firestore!');

      setMobileNumber(formatPhoneNumber(numbers)); // Display formatted
      setIsEditingPhone(false);
      setSuccessMessage('Mobile number updated successfully! Reloading page...');
      
      // Reload the page after a brief delay to show success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('❌ Error updating mobile number:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      console.error('❌ Full error:', JSON.stringify(error, null, 2));
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        setErrorMessage('Permission denied. Please contact your administrator to update security rules.');
      } else if (error.code === 'not-found') {
        setErrorMessage('User record not found. Please contact support.');
      } else {
        setErrorMessage(`Failed to update mobile number: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSmsToggle = async () => {
    if (!currentUser.mobileNumber && !mobileNumber) {
      setErrorMessage('Please add a mobile number before enabling SMS notifications.');
      return;
    }

    console.log('📱 Starting SMS toggle...');
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const newOptInValue = !smsOptIn;
      
      // Determine which collection the user is in
      const collectionName = currentUser.userType === 'volunteer' ? 'volunteers' : 'faculty_staff';
      const documentId = currentUser.firestoreDocId || currentUser.docId;

      console.log('📱 SMS toggle details:', {
        collection: collectionName,
        documentId: documentId,
        newValue: newOptInValue
      });

      if (!documentId) {
        console.error('❌ No document ID found!');
        setErrorMessage('Error: Cannot identify your user record. Please contact support.');
        setLoading(false);
        return;
      }

      const userRef = doc(db, collectionName, documentId);
      
      await updateDoc(userRef, {
        smsOptIn: newOptInValue,
        smsOptInDate: newOptInValue ? serverTimestamp() : null,
      });

      console.log('✅ SMS preference updated successfully!');

      setSmsOptIn(newOptInValue);
      setSuccessMessage(`SMS notifications ${newOptInValue ? 'enabled' : 'disabled'} successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('❌ Error updating SMS preference:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        setErrorMessage('Permission denied. Please contact your administrator to update security rules.');
      } else {
        setErrorMessage(`Failed to update SMS preference: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-settings-container">
      <div className="settings-header">
        <h1>User Settings</h1>
        <p>Manage your account preferences and notification settings</p>
      </div>

      {/* Account Information Section */}
      <div className="settings-section">
        <h2>Account Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <p>{currentUser.displayName || currentUser.name || 'Not set'}</p>
          </div>
          <div className="info-item">
            <label>Email</label>
            <p className="email-wrap">{currentUser.email}</p>
          </div>
          <div className="info-item">
            <label>Role</label>
            <p>{currentUser.role}</p>
          </div>
          <div className="info-item">
            <label>Department</label>
            <p>{currentUser.department || 'Not set'}</p>
          </div>
        </div>
      </div>

      {/* Phone Number Section */}
      <div className="settings-section">
        <h2>Mobile Phone Number</h2>
        
        {successMessage && (
          <div className="success-message">
            <span>✓</span> {successMessage}
          </div>
        )}
        
        {errorMessage && (
          <div className="error-message">
            <span>⚠</span> {errorMessage}
          </div>
        )}

        <div className="phone-section">
          <p className="section-description">
            Your mobile phone number is used for SMS notifications. Make sure this is a mobile number, not a landline.
          </p>

          {!isEditingPhone ? (
            <div className="phone-display-box">
              <div className="phone-info">
                <label>Current Mobile Number</label>
                <p className="phone-number">
                  {currentUser.mobileNumber ? formatPhoneNumber(currentUser.mobileNumber.replace('+1', '')) : 'No mobile number on file'}
                </p>
              </div>
              <button 
                className="btn-edit-phone"
                onClick={() => setIsEditingPhone(true)}
              >
                {currentUser.mobileNumber ? 'Update Number' : 'Add Number'}
              </button>
            </div>
          ) : (
            <div className="phone-edit-box">
              <label htmlFor="phoneInput">Mobile Phone Number</label>
              <input
                id="phoneInput"
                type="tel"
                value={mobileNumber}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
                maxLength={14}
                className="phone-input"
              />
              <p className="help-text">Enter a 10-digit US mobile phone number</p>
              
              <div className="phone-button-group">
                <button 
                  className="btn-save"
                  onClick={handleSavePhone}
                  disabled={loading || !mobileNumber}
                >
                  {loading ? 'Saving...' : 'Save Number'}
                </button>
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setIsEditingPhone(false);
                    setMobileNumber(currentUser.mobileNumber ? formatPhoneNumber(currentUser.mobileNumber.replace('+1', '')) : '');
                    setErrorMessage('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notification Settings Section */}
      <div className="settings-section">
        <h2>Notification Settings</h2>

        <div className="notification-option">
          <div className="notification-info">
            <h3>Text Message Notifications</h3>
            <p className="notification-description">
              Receive SMS notifications for job opportunities, absence confirmations, and schedule updates.
            </p>
          </div>
          
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={smsOptIn}
              onChange={handleSmsToggle}
              disabled={loading || (!currentUser.mobileNumber && !mobileNumber)}
            />
            <span className="slider"></span>
          </label>
        </div>

        {smsOptIn && (
          <div className="sms-info-box">
            <p><strong>You will receive text messages for:</strong></p>
            <ul>
              {currentUser.role !== 'Volunteer' && (
                <>
                  <li>New substitute teaching job opportunities</li>
                  <li>Absence request confirmations</li>
                  <li>Schedule changes and updates</li>
                </>
              )}
              {(currentUser.role === 'Volunteer' || currentUser.hasVolunteerAccess) && (
                <>
                  <li>New volunteer event opportunities</li>
                  <li>Event reminders and updates</li>
                  <li>Volunteer schedule confirmations</li>
                </>
              )}
              <li>Important administrative notifications</li>
            </ul>
            <p className="sms-disclaimer">
              Standard message and data rates may apply. Reply STOP to any message to unsubscribe.
            </p>
          </div>
        )}
      </div>

      {/* Additional Settings Section - Placeholder for future features */}
      <div className="settings-section">
        <h2>Preferences</h2>
        <div className="coming-soon">
          <p>Additional preferences and settings will be available here soon.</p>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;